import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Check, AlertTriangle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { createApiKey, fetchAvailableScopes } from '../api/apiKeys'
import type { ApiKeyGenerationResponse, CreateApiKeyRequest, KeyType } from '../types/apiKey.types'

interface Props {
  accountId?: string
  onClose: () => void
  onCreated?: (keyId: string, secret: string) => void
}

const SCOPE_GROUPS = [
  {
    label: 'Event Ingestion',
    scopes: ['EVENT_INGESTION:PROCESS', 'EVENT_INGESTION:VIEW', 'EVENT_INGESTION:UPLOAD'],
  },
  {
    label: 'Datalist Ingestion',
    scopes: [
      'DATALIST_INGESTION:UPLOAD',
      'DATALIST_INGESTION:PROCESS',
      'DATALIST_INGESTION:READ',
      'DATALIST_INGESTION:LIST',
      'DATALIST_INGESTION:UPDATE',
      'DATALIST_INGESTION:DELETE',
    ],
  },
  {
    label: 'Notification Service',
    scopes: ['NOTIFICATION_SERVICE:EXECUTE'],
  },
  {
    label: 'Push Notification',
    scopes: ['PUSH_NOTIFICATION:EXECUTE'],
  },
]

export function CreateApiKeyModal({ accountId = '', onClose, onCreated }: Props) {
  const queryClient = useQueryClient()

  // Form state
  const [name, setName] = useState('')
  const [keyType, setKeyType] = useState<KeyType>('TEST')
  const [description, setDescription] = useState('')
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set())
  const [allScopes, setAllScopes] = useState(false)

  // Post-creation reveal state
  const [created, setCreated] = useState<ApiKeyGenerationResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const { data: availableScopes = [] } = useQuery({
    queryKey: ['api-key-scopes'],
    queryFn: fetchAvailableScopes,
    staleTime: Infinity,
  })

  const mutation = useMutation({
    mutationFn: (data: CreateApiKeyRequest) => createApiKey(accountId, data),
    onSuccess: (result) => {
      setCreated(result)
      queryClient.invalidateQueries({ queryKey: ['api-keys', accountId] })
    },
  })

  const toggleScope = (scope: string) => {
    const next = new Set(selectedScopes)
    next.has(scope) ? next.delete(scope) : next.add(scope)
    setSelectedScopes(next)
    setAllScopes(false)
  }

  const handleToggleAll = () => {
    if (allScopes) {
      setSelectedScopes(new Set())
      setAllScopes(false)
    } else {
      setSelectedScopes(new Set(availableScopes))
      setAllScopes(true)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: CreateApiKeyRequest = {
      name: name.trim(),
      keyType,
      description: description.trim() || undefined,
      scopes: selectedScopes.size > 0 ? Array.from(selectedScopes) : undefined,
    }
    mutation.mutate(payload)
  }

  const handleCopy = async () => {
    if (!created) return
    await navigator.clipboard.writeText(created.secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDone = () => {
    if (!confirmed || !created) return
    onCreated?.(created.keyId, created.secret)
    onClose()
  }

  // ── Secret reveal screen ──────────────────────────────────────────────────
  if (created) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">API Key Created</h2>
              <p className="text-sm text-gray-500">{created.name} ({created.keyType})</p>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800 font-medium">{created.warning}</p>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 mb-1">Key ID</p>
            <p className="font-mono text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{created.keyId}</p>
          </div>

          <div className="mb-6">
            <p className="text-xs font-medium text-gray-500 mb-1">Secret (copy now — never shown again)</p>
            <div className="flex gap-2">
              <code className="flex-1 font-mono text-sm bg-gray-900 text-green-400 rounded px-3 py-2 overflow-auto break-all">
                {created.secret}
              </code>
              <Button variant="outline" size="icon" onClick={handleCopy} title="Copy secret">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">I have securely saved the secret</span>
          </label>

          <Button onClick={handleDone} disabled={!confirmed} className="w-full">
            Done
          </Button>
        </div>
      </div>
    )
  }

  // ── Create form ───────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Create API Key</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Integration"
              maxLength={100}
              required
            />
          </div>

          {/* Key Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Key Type</label>
            <div className="flex gap-4">
              {(['TEST', 'LIVE'] as KeyType[]).map((t) => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="keyType"
                    value={t}
                    checked={keyType === t}
                    onChange={() => setKeyType(t)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-700">
                    {t === 'TEST' ? 'TEST — sandbox/test' : 'LIVE — production'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this key used for?"
              maxLength={500}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>

          {/* Scopes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Scopes{' '}
                <span className="text-gray-400 font-normal">(empty = full access)</span>
              </label>
              <button
                type="button"
                onClick={handleToggleAll}
                className="text-xs text-primary hover:underline"
              >
                {allScopes ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="rounded-lg border border-gray-200 divide-y">
              {SCOPE_GROUPS.map((group) => (
                <div key={group.label} className="p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{group.label}</p>
                  <div className="space-y-1.5">
                    {group.scopes.map((scope) => (
                      <label key={scope} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedScopes.has(scope)}
                          onChange={() => toggleScope(scope)}
                          className="rounded border-gray-300 accent-primary"
                        />
                        <span className="font-mono text-xs text-gray-700">{scope}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create Key'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
